import { useQuery } from '@tanstack/react-query'

import { useAuth } from '../auth/AuthProvider'
import {
  getOnboardingApiClient,
  OnboardingApiError,
} from '../onboarding/onboarding-api'
import type { ClinicAlert } from './organization-alert-contracts'

/**
 * The header asks once per clinic, then again every five minutes while the
 * page is visible (and on focus after that), never while it is hidden. An
 * Administrator's save refreshes every clinic's answer in their session at
 * once; other users see a change within five minutes.
 */
export const ORGANIZATION_ALERT_STALE_MS = 5 * 60_000

export const organizationAlertKeys = {
  all: ['organization-alert'] as const,
  clinic: (tenantId: string) =>
    ['organization-alert', 'clinic', tenantId] as const,
  administrator: (organizationId: string) =>
    ['organization-alert', 'administrator', organizationId] as const,
}

function transient(error: unknown) {
  if (!(error instanceof OnboardingApiError)) return true
  return (
    error.code === 'transport' ||
    error.status === null ||
    error.status === 429 ||
    error.status >= 500
  )
}

export type OrganizationAlertReader = {
  getClinicAlert: (tenantId: string) => Promise<ClinicAlert>
}

export function useClinicOrganizationAlert(
  tenantId: string | null,
  reader?: OrganizationAlertReader
) {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const query = useQuery({
    queryKey: organizationAlertKeys.clinic(tenantId ?? ''),
    queryFn: () =>
      reader
        ? reader.getClinicAlert(tenantId!)
        : getOnboardingApiClient().getClinicOrganizationAlert!(
            user!,
            tenantId!
          ),
    enabled: tenantId !== null && user !== null,
    // Only a transport or server failure is retried, once. A refusal (no
    // access, not deployed) waits for the next five-minute read.
    retry: (failures, error) => failures < 1 && transient(error),
    staleTime: ORGANIZATION_ALERT_STALE_MS,
    // The shell stays mounted across pages, so the answer is refreshed on a
    // slow timer while visible; a failed read is not retried on every focus.
    refetchInterval: ORGANIZATION_ALERT_STALE_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (current) => current.state.status !== 'error',
  })
  // React Query keeps the last answer after a failed refresh; the header does
  // not. A refused or failed read (lost access, an outage) hides the alert
  // until a later read succeeds. An answer for another clinic is never shown,
  // even briefly while switching.
  if (query.isError) return null
  return query.data && query.data.tenantUid === tenantId
    ? query.data.alert
    : null
}
